import { type RefObject, useCallback, useEffect } from 'react';

import type { TrackType } from '../../api/cc-smarts/models/TrackType';
import { useAudioPlayerStreamingContext } from '../context/AudioPlayerStreamingProvider';
import { setActiveContent } from '../state/AudioPlayerState';
import type { PlaylistItemData } from '../ui/playlist/PlaylistItem';

import { useAudioPlayerStore } from './useAudioPlayerStore';
import { useRemoveTrack } from './useRemoveTrack';

type UsePlaylistAutoPlayProps = {
	audioRef: RefObject<HTMLAudioElement>;
	playlistItems: PlaylistItemData[];
	currentItemId: string | undefined;
};

export const usePlaylistAutoPlay = ({
	audioRef,
	playlistItems,
	currentItemId,
}: UsePlaylistAutoPlayProps) => {
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const { removeTrack } = useRemoveTrack();
	const { cloudId } = useAudioPlayerStore();

	const playNextTrack = useCallback(async () => {
		if (playlistItems.length === 0) {
			return;
		}

		const currentIndex = playlistItems.findIndex((item) => item.id === currentItemId);

		// Get the current item to remove it
		const currentItem = currentIndex !== -1 ? playlistItems[currentIndex] : null;
		// Remove the finished track if we have the necessary data
		if (currentItem && currentItem.mainSource) {
			try {
				const mainSource = currentItem.mainSource;
				const supportingSources = currentItem.supportingSources || [];
				const trackType = currentItem.type as TrackType;

				await removeTrack(cloudId, mainSource, supportingSources, trackType);
			} catch (error) {
				// Silently ignore errors - continue to next track even if removal fails
				// leaving this for analytics instrumentation
			}
		}

		let nextTrack: PlaylistItemData;

		if (currentIndex === -1) {
			nextTrack = playlistItems[0];
		} else if (currentIndex === playlistItems.length - 1) {
			// No more tracks to play
			return;
		} else {
			nextTrack = playlistItems[currentIndex + 1];
		}

		setActiveContent({
			contentId: nextTrack.id,
			spaceKey: nextTrack.spaceKey || '',
			contentType: nextTrack.contentType || '',
			title: nextTrack.title || '',
			author: nextTrack.author || '',
			avatarUrl: '',
		});

		fetchAudio();
	}, [currentItemId, playlistItems, fetchAudio, removeTrack, cloudId]);

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio) {
			return;
		}

		const onEnded = () => {
			playNextTrack();
		};

		audio.addEventListener('ended', onEnded);

		return () => {
			audio.removeEventListener('ended', onEnded);
		};
	}, [audioRef, playNextTrack]);
};
